import { Box, Container, Heading, Text, VStack, HStack, Tag, Badge, Flex } from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { usePublicHelpArticles } from '../hooks/useHelpArticles';
import { usePublicHelpCategories } from '../hooks/useHelpCategories';

export default function HelpCategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [lang, setLang] = useState<'fr' | 'en'>('fr');
  const navigate = useNavigate();

  /**
   * Both reads take `isError`, because either one failing produced the same
   * confident lie. With `categories` undefined, `category` is undefined, so
   * `categoryArticles` filters on `a.categoryId === undefined` and comes back
   * empty; with `articles` undefined the `|| []` does it directly. The page
   * then printed *"Aucun article dans cette catégorie."* under a heading stuck
   * at `...` — a statement about the category's contents, made by a page that
   * could not read either the category or its articles.
   *
   * Neither route is guarded (`@Get('categories')` / `@Get('articles')`,
   * `api/src/help-center/help-center.controller.ts`), so this is the 5xx and
   * network path, on the surface a user reaches when something else is broken.
   */
  const {
    data: categories,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = usePublicHelpCategories(lang);
  const {
    data: articles,
    isLoading: articlesLoading,
    isError: articlesError,
  } = usePublicHelpArticles(lang);

  const isLoading = categoriesLoading || articlesLoading;
  /**
   * Error AND no data, per read. React Query keeps the last good `data` when a
   * BACKGROUND refetch fails, and a page that replaces a correct article list
   * with an alert because a refresh failed is the mirror of the defect being
   * fixed here.
   */
  const isError = (categoriesError && !categories) || (articlesError && !articles);
  const category = categories?.find((c) => c.slug === categorySlug);
  const categoryArticles = articles?.filter((a) => a.categoryId === category?.id) || [];

  /**
   * `...` is the placeholder for "still loading". It must not also stand for
   * "we could not read this", which is how the heading and the breadcrumb read
   * on a failure — indefinitely, since nothing is in flight any more.
   */
  const categoryLabel = category
    ? lang === 'en'
      ? category.nameEn
      : category.nameFr
    : isError
      ? lang === 'fr'
        ? 'Rubrique indisponible'
        : 'Topic unavailable'
      : '...';

  return (
    <Container maxW="800px" py={12}>
      {/* Language Toggle */}
      <HStack justify="flex-end" mb={6}>
        <Tag size="md" variant={lang === 'fr' ? 'solid' : 'outline'} colorScheme="brand" cursor="pointer" onClick={() => setLang('fr')}>FR</Tag>
        <Tag size="md" variant={lang === 'en' ? 'solid' : 'outline'} colorScheme="brand" cursor="pointer" onClick={() => setLang('en')}>EN</Tag>
      </HStack>

      {/* Breadcrumb */}
      <HStack mb={6} fontSize="sm" color="gray.500">
        <Text cursor="pointer" _hover={{ color: 'brand.500' }} onClick={() => navigate('/marketing/help')}>
          {lang === 'fr' ? 'Centre d\'aide' : 'Help Center'}
        </Text>
        <Text>/</Text>
        <Text fontWeight="medium" color="gray.700">
          {categoryLabel}
        </Text>
      </HStack>

      <Heading size="lg" mb={2}>
        {categoryLabel}
      </Heading>
      {category && (lang === 'en' ? category.descriptionEn : category.descriptionFr) && (
        <Text color="gray.600" mb={8}>
          {lang === 'en' ? category.descriptionEn : category.descriptionFr}
        </Text>
      )}

      {/* Article List */}
      <VStack align="stretch" spacing={3}>
        {categoryArticles.map((article) => (
          <Box
            key={article.id}
            p={4}
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ borderColor: 'brand.300', bg: 'gray.50' }}
            onClick={() => navigate(`/marketing/help/${categorySlug}/${article.slug}`)}
          >
            <Text fontWeight="medium">{article.title}</Text>
            {article.excerpt && (
              <Text color="gray.500" fontSize="sm" mt={1}>{article.excerpt}</Text>
            )}
          </Box>
        ))}
        {isError && (
          <InlineErrorState
            message={
              lang === 'fr'
                ? 'Impossible de charger cette rubrique pour le moment. Veuillez r\u00e9essayer.'
                : 'We could not load this topic right now. Please try again.'
            }
          />
        )}
        {!isError && isLoading && (
          <Text color="gray.500" textAlign="center" py={8}>Loading...</Text>
        )}
        {!isError && !isLoading && categoryArticles.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>
            {lang === 'fr' ? 'Aucun article dans cette cat\u00e9gorie.' : 'No articles in this category.'}
          </Text>
        )}
      </VStack>
    </Container>
  );
}
